import React from 'react';
import PropTypes from 'prop-types';
import { connect } from '@gasbuddy/react';
import { FlexGrid } from '@gasbuddy/react-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

import MainLayout from '../../components/main-layout';
import CTAContainer from '../../components/cta-container';
import Button from '../../components/button';
import Notification from '../../components/notification';
import Table from '../../components/table';
import BPUtils from '../../../lib/bputils';
import ReviewReplyModal from '../../components/review-reply-modal';
import HowToRespondButton from '../../components/reputation-tab/how-to-respond-button';
import { ReplyTemplatePropType } from '../../components/review-reply-modal/prop-types';

import styles from './styles.css';

const ReplyTemplateCTA = ({ template, onClick = () => { } }) => (
  <Button secondary size="sm" onClick={() => onClick(template)}>
    {`${template.editable ? 'Edit' : 'View'} Template`}
  </Button>
);

ReplyTemplateCTA.propTypes = {
  template: ReplyTemplatePropType.isRequired,
  onClick: PropTypes.func,
};

ReplyTemplateCTA.defaultProps = {
  onClick: () => { },
};

function ReviewReplies() {
  const [showModal, setShowModal] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [replyTemplates, setReplyTemplates] = React.useState([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [replyCategories, setReplyCategories] = React.useState([]);

  React.useEffect(() => {
    setIsLoading(true);
    getReplyTemplates().then(() => setIsLoading(false));
    getReplyCategories();
  }, []);

  const hanndleOnEditTemplateClick = (template) => {
    setShowModal(true);
    setSelectedTemplate(template);
  };

  const handleOnTemplateClose = () => {
    setSelectedTemplate(null);
    setShowModal(false);
  };

  const handleOnTemplateSave = (template) => {
    setIsLoading(true);
    if (template.id) {
      setIsLoading(true);
      const oldTemplateIndex = replyTemplates.findIndex(rt => rt.id === template.id);
      replyTemplates[oldTemplateIndex] = template;
      setReplyTemplates(replyTemplates);
      setTimeout(() => setIsLoading(false), 500);
    } else {
      setIsLoading(true);
      getReplyTemplates().then(() => setIsLoading(false));
    }
  };

  const handleOnTemplateDelete = (templateId) => {
    setIsLoading(true);
    const deletedTemplateIndex = replyTemplates.findIndex(rt => rt.id === templateId);
    replyTemplates.splice(deletedTemplateIndex, 1);
    setTimeout(() => setIsLoading(false), 500);
    setReplyTemplates(replyTemplates);
  };

  const REPLY_TEMPLATE_COLUMNS = [
    {
      title: 'Category',
      value: template => template.category,
      colSpan: 1,
    },
    {
      title: 'Title',
      value: template => template.title,
      colSpan: 1,
    },
    {
      title: 'Message',
      value: template => <div style={{ whiteSpace: 'break-spaces' }}>{template.message}</div>,
      colSpan: 2,
    },
    {
      title: '',
      render: template => ReplyTemplateCTA({ template, onClick: hanndleOnEditTemplateClick }),
      value: template => `template_${template.id}`,
      colSpan: 1,
    },
  ];

  const getReplyTemplates = async () => {
    await BPUtils.api.get({
      path: '/reviews/replies/templates',
      requestId: 'getReplyTemplates',
      success: (res) => {
        setReplyTemplates((res || []));
        setIsLoading(false);
      },
      error: () => {
        Notification.error('Unable to fetch reply templates');
      },
    });
  };

  const getReplyCategories = async () => {
    await BPUtils.api.get({
      path: '/reviews/replies/templates/categories',
      requestId: 'getReplyTemplateCategories',
      success: (res) => {
        setReplyCategories((res || []));
      },
      error: () => {
        Notification.error('Unable to fetch reply categories');
      },
    });
  };

  return (
    <MainLayout title="Review Reply Templates">
      <CTAContainer alignment="right">
        <FlexGrid.Column>
          <Button primary onClick={() => setShowModal(true)} size="sm">
            <FontAwesomeIcon icon={faPlus} />
                  &nbsp;&nbsp;
            <span>Create a Template</span>
          </Button>
        </FlexGrid.Column>
      </CTAContainer>
      <div className={styles.replyTemplateTipContainer}>
        <div>
          <div className={styles.text}>
            <b>Not sure how to respond to customer reviews?</b>
          </div>
          <div className={styles.text}>
            We’ve got some helpful tips to get you get started.
          </div>
        </div>
        <div className={styles.replyTipIconContainer}>
          <HowToRespondButton />
        </div>
      </div>
      <FlexGrid.Column>
        <Table title="My Templates" loading={isLoading} tableColumns={REPLY_TEMPLATE_COLUMNS} data={replyTemplates} />
      </FlexGrid.Column>
      {replyCategories.length ? (
        <ReviewReplyModal
          template={selectedTemplate}
          show={showModal}
          onSave={handleOnTemplateSave}
          onClose={handleOnTemplateClose}
          onDelete={handleOnTemplateDelete}
          replyCategories={replyCategories}
        />
      ) : null}
    </MainLayout>
  );
}

export default connect()(ReviewReplies);
